import { useState, useEffect } from 'react';
import axios from 'axios';

// Data Layer
import { RequestIndicatorTypes } from 'data-layer-cr/dist/models';

// Network
import processRequest from '../../services/processRequest';
import PdfClient from '../../services/PdfClient';

/**
 * A hook that retrieves a pdf blob from the provided url, and exposes it
 * via the `pdfBlob` property.
 *
 * @param url - The pdf's url
 */
export default function usePdfDownloader(url: string): {
  pdfBlob: Blob | undefined;
  fetchStatus: RequestIndicatorTypes;
} {
  const [pdfBlob, updatePdfBlob] = useState<Blob>();
  const [fetchStatus, updateFetchStatus] =
    useState<RequestIndicatorTypes>('initial');

  useEffect(() => {
    /**
     * This token source will be used to cancel any pending requests on unmount, preventing
     * any possible memory leaks.
     */
    const source = axios.CancelToken.source();

    async function fetchPdf(): Promise<void> {
      updateFetchStatus('requesting');
      try {
        const data = await processRequest<Blob>({
          method: 'GET',
          url,
          client: PdfClient,
          cancelTokenSource: source,
        });
        updatePdfBlob(data);
        updateFetchStatus('success');
      } catch (e) {
        /**
         * Don't update fetching status for cancelled requests, as this means that the
         * component was unmounted. Not doing so causes it to try to force a render after
         * being unmounted.
         */
        if (!axios.isCancel(e)) {
          updateFetchStatus('failure');
        }
      }
    }
    fetchPdf();

    /**
     * Make sure that the request cancels when the component is unmounted, otherwise
     * we risk trying to update an unmounted component.
     */
    return (): void => source.cancel();
  }, [url]);

  return {
    pdfBlob,
    fetchStatus,
  };
}
